import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { inventoryAPI, propertyAPI, roomChargeAPI, reservationAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Package, Plus, Trash2, Pencil, AlertTriangle, ArrowUpCircle, ArrowDownCircle,
  Loader2, Search, Filter, ShoppingCart, History, Bed, X
} from 'lucide-react';

const DEFAULT_CATEGORIES = ['Minibar', 'Amenities', 'Lavandería', 'Room Service', 'Snacks', 'Licores', 'Limpieza'];

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function BodegaPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [activeTab, setActiveTab] = useState('productos');

  // Product Dialog
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    nombre: '', categoria: '', precio_venta: '', costo: '', cantidad_inicial: '', stock_minimo: '5', unidad: 'Unidad', descripcion: ''
  });
  const [newCategory, setNewCategory] = useState('');
  const [saving, setSaving] = useState(false);

  // Stock adjustment dialog
  const [stockDialog, setStockDialog] = useState(false);
  const [stockTarget, setStockTarget] = useState(null);
  const [stockForm, setStockForm] = useState({ cantidad: '', motivo: 'compra', tipo: 'entrada' });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Charge dialog (for charging to rooms)
  const [chargeDialog, setChargeDialog] = useState(false);
  const [chargeProduct, setChargeProduct] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [chargeForm, setChargeForm] = useState({ reservation_id: '', cantidad: '1', concepto: 'Consumo' });

  useEffect(() => {
    propertyAPI.getAll().then(res => {
      setProperties(res.data);
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedProperty) return;
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        inventoryAPI.getProducts(selectedProperty),
        inventoryAPI.getCategories(selectedProperty)
      ]);
      setProducts(prodRes.data);
      const mergedCats = [...new Set([...DEFAULT_CATEGORIES, ...catRes.data])];
      setCategories(mergedCats);
    } catch { toast.error('Error cargando inventario'); }
    finally { setLoading(false); }
  }, [selectedProperty]);

  const loadMovements = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await inventoryAPI.getMovements(selectedProperty);
      setMovements(res.data);
    } catch { /* ignore */ }
  }, [selectedProperty]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 'historial') loadMovements(); }, [activeTab, loadMovements]);

  // Load active reservations for charge dialog
  const loadReservations = async () => {
    try {
      const res = await reservationAPI.getAll(selectedProperty);
      setReservations(res.data.filter(r => r.estado === 'checked_in' || r.estado === 'confirmada'));
    } catch { /* ignore */ }
  };

  // Filtered products
  const filtered = products.filter(p => {
    const matchSearch = p.nombre.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || p.categoria === filterCat;
    return matchSearch && matchCat;
  });

  const lowStockCount = products.filter(p => p.stock_actual < (p.stock_minimo || 5)).length;

  // CRUD Handlers
  const openCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({ nombre: '', categoria: '', precio_venta: '', costo: '', cantidad_inicial: '', stock_minimo: '5', unidad: 'Unidad', descripcion: '' });
    setNewCategory('');
    setProductDialog(true);
  };

  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({
      nombre: p.nombre, categoria: p.categoria, precio_venta: String(p.precio_venta),
      costo: String(p.costo || 0), cantidad_inicial: '', stock_minimo: String(p.stock_minimo || 5),
      unidad: p.unidad, descripcion: p.descripcion || ''
    });
    setNewCategory('');
    setProductDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.nombre || !productForm.precio_venta) { toast.error('Nombre y precio son requeridos'); return; }
    const cat = newCategory.trim() || productForm.categoria;
    if (!cat) { toast.error('Selecciona o crea una categoría'); return; }
    setSaving(true);
    try {
      if (editingProduct) {
        await inventoryAPI.updateProduct(editingProduct.id, {
          nombre: productForm.nombre, categoria: cat,
          precio_venta: parseFloat(productForm.precio_venta), costo: parseFloat(productForm.costo || 0),
          stock_minimo: parseInt(productForm.stock_minimo || 5), unidad: productForm.unidad,
          descripcion: productForm.descripcion
        });
        toast.success('Producto actualizado');
      } else {
        if (!productForm.cantidad_inicial) { toast.error('Cantidad inicial requerida'); setSaving(false); return; }
        await inventoryAPI.createProduct({
          property_id: selectedProperty, nombre: productForm.nombre, categoria: cat,
          precio_venta: parseFloat(productForm.precio_venta), costo: parseFloat(productForm.costo || 0),
          cantidad_inicial: parseInt(productForm.cantidad_inicial), stock_minimo: parseInt(productForm.stock_minimo || 5),
          unidad: productForm.unidad, descripcion: productForm.descripcion
        });
        toast.success('Producto creado');
      }
      setProductDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDeleteProduct = async () => {
    if (!deleteTarget) return;
    try {
      await inventoryAPI.deleteProduct(deleteTarget.id);
      toast.success('Producto eliminado');
      setDeleteDialog(false);
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAdjustStock = async () => {
    if (!stockTarget || !stockForm.cantidad) return;
    const qty = parseInt(stockForm.cantidad);
    if (isNaN(qty) || qty <= 0) { toast.error('Cantidad debe ser mayor a 0'); return; }
    try {
      const amount = stockForm.tipo === 'entrada' ? qty : -qty;
      await inventoryAPI.adjustStock(stockTarget.id, { cantidad: amount, motivo: stockForm.motivo });
      toast.success(`Stock ${stockForm.tipo === 'entrada' ? 'aumentado' : 'reducido'} en ${qty}`);
      setStockDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Charge to room
  const openChargeDialog = (product) => {
    setChargeProduct(product);
    setChargeForm({ reservation_id: '', cantidad: '1', concepto: 'Consumo ' + product.nombre });
    loadReservations();
    setChargeDialog(true);
  };

  const handleCharge = async () => {
    if (!chargeProduct || !chargeForm.reservation_id) { toast.error('Selecciona una habitación'); return; }
    const qty = parseInt(chargeForm.cantidad);
    if (isNaN(qty) || qty < 1) { toast.error('Cantidad inválida'); return; }
    try {
      await roomChargeAPI.create({
        reservation_id: chargeForm.reservation_id, product_id: chargeProduct.id,
        cantidad: qty, precio_unitario: chargeProduct.precio_venta,
        concepto: chargeForm.concepto, notas: null
      });
      toast.success(`${chargeProduct.nombre} x${qty} cargado a habitación`);
      setChargeDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cargar'); }
  };

  const pf = (n) => `$${(n || 0).toLocaleString('es-CO')}`;

  if (loading && products.length === 0) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="bodega-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="bodega-title">
              <Package className="h-6 w-6 text-amber-600" />
              Bodega / Inventario
            </h1>
            <p className="text-muted-foreground mt-1">Gestiona productos, stock y consumos por habitación</p>
          </div>
          <div className="flex items-center gap-3">
            {properties.length > 1 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[200px]" data-testid="bodega-property-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button onClick={openCreateProduct} className="bg-amber-600 hover:bg-amber-700" data-testid="create-product-btn">
              <Plus className="h-4 w-4 mr-2" />Nuevo Producto
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">Productos</p>
            <p className="text-2xl font-bold" data-testid="total-products">{products.length}</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">Categorías</p>
            <p className="text-2xl font-bold">{[...new Set(products.map(p => p.categoria))].length}</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">Valor Inventario</p>
            <p className="text-2xl font-bold text-green-600">{pf(products.reduce((s, p) => s + p.precio_venta * p.stock_actual, 0))}</p>
          </CardContent></Card>
          <Card className={lowStockCount > 0 ? 'border-red-200 bg-red-50/50' : ''}>
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground">Stock Bajo</p>
              <p className={`text-2xl font-bold ${lowStockCount > 0 ? 'text-red-600' : ''}`} data-testid="low-stock-count">
                {lowStockCount > 0 && <AlertTriangle className="h-4 w-4 inline mr-1" />}{lowStockCount}
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="productos" data-testid="tab-productos"><Package className="h-4 w-4 mr-1" />Productos</TabsTrigger>
            <TabsTrigger value="historial" data-testid="tab-historial"><History className="h-4 w-4 mr-1" />Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="productos" className="space-y-3">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Buscar producto..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-8" data-testid="product-search" />
              </div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <Filter className="h-4 w-4 mr-1" /><SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Products Table */}
            {filtered.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-muted-foreground">{products.length === 0 ? 'No hay productos. Crea el primero.' : 'No se encontraron productos con ese filtro.'}</p>
              </CardContent></Card>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium">Producto</th>
                        <th className="text-left px-4 py-2.5 font-medium">Categoría</th>
                        <th className="text-right px-4 py-2.5 font-medium">P. Venta</th>
                        <th className="text-right px-4 py-2.5 font-medium">Costo</th>
                        <th className="text-center px-4 py-2.5 font-medium">Stock</th>
                        <th className="text-right px-4 py-2.5 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filtered.map(p => {
                        const lowStock = p.stock_actual < (p.stock_minimo || 5);
                        return (
                          <tr key={p.id} className={`hover:bg-gray-50 ${lowStock ? 'bg-red-50/40' : ''}`} data-testid={`product-row-${p.id}`}>
                            <td className="px-4 py-2.5">
                              <span className="font-medium">{p.nombre}</span>
                              {p.descripcion && <p className="text-xs text-gray-400 truncate max-w-[200px]">{p.descripcion}</p>}
                            </td>
                            <td className="px-4 py-2.5"><Badge variant="outline" className="text-xs">{p.categoria}</Badge></td>
                            <td className="px-4 py-2.5 text-right font-medium">{pf(p.precio_venta)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{pf(p.costo)}</td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`inline-flex items-center gap-1 font-semibold ${lowStock ? 'text-red-600' : 'text-green-700'}`}>
                                {lowStock && <AlertTriangle className="h-3.5 w-3.5" />}
                                {p.stock_actual} {p.unidad}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button size="sm" variant="ghost" title="Cargar a habitación"
                                  onClick={() => openChargeDialog(p)} data-testid={`charge-btn-${p.id}`}>
                                  <Bed className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" title="Ajustar stock"
                                  onClick={() => { setStockTarget(p); setStockForm({ cantidad: '', motivo: 'compra', tipo: 'entrada' }); setStockDialog(true); }}
                                  data-testid={`stock-btn-${p.id}`}>
                                  <ArrowUpCircle className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEditProduct(p)} data-testid={`edit-btn-${p.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-500"
                                  onClick={() => { setDeleteTarget(p); setDeleteDialog(true); }} data-testid={`delete-btn-${p.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="historial">
            {movements.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-muted-foreground">No hay movimientos registrados</p>
              </CardContent></Card>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                      <th className="text-left px-4 py-2.5 font-medium">Producto</th>
                      <th className="text-center px-4 py-2.5 font-medium">Tipo</th>
                      <th className="text-center px-4 py-2.5 font-medium">Cantidad</th>
                      <th className="text-left px-4 py-2.5 font-medium">Motivo</th>
                      <th className="text-left px-4 py-2.5 font-medium">Usuario</th>
                      <th className="text-left px-4 py-2.5 font-medium">Habitación</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {movements.map(m => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-xs">{new Date(m.created_at).toLocaleString('es-CO')}</td>
                        <td className="px-4 py-2.5 font-medium">{m.product_name || m.product_id?.slice(0, 8)}</td>
                        <td className="px-4 py-2.5 text-center">
                          {m.tipo === 'entrada'
                            ? <Badge className="bg-green-100 text-green-800 border-green-200"><ArrowUpCircle className="h-3 w-3 mr-1" />Entrada</Badge>
                            : <Badge className="bg-red-100 text-red-800 border-red-200"><ArrowDownCircle className="h-3 w-3 mr-1" />Salida</Badge>}
                        </td>
                        <td className="px-4 py-2.5 text-center font-semibold">{m.cantidad}</td>
                        <td className="px-4 py-2.5 capitalize text-gray-600">{m.motivo?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2.5 text-gray-600">{m.user_name || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{m.room_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* CREATE/EDIT PRODUCT DIALOG */}
        <Dialog open={productDialog} onOpenChange={setProductDialog}>
          <DialogContent className="max-w-lg" data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label>Nombre *</Label>
                  <Input value={productForm.nombre} onChange={e => setProductForm(p => ({ ...p, nombre: e.target.value }))}
                    placeholder="Ej: Agua mineral 600ml" data-testid="product-form-nombre" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Categoría *</Label>
                  <Select value={productForm.categoria} onValueChange={v => setProductForm(p => ({ ...p, categoria: v }))}>
                    <SelectTrigger data-testid="product-form-categoria"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-1">
                    <Input placeholder="O crear nueva categoría..." value={newCategory}
                      onChange={e => setNewCategory(e.target.value)} className="text-xs" data-testid="new-category-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Precio Venta *</Label>
                  <Input type="number" value={productForm.precio_venta}
                    onChange={e => setProductForm(p => ({ ...p, precio_venta: e.target.value }))}
                    placeholder="5000" data-testid="product-form-precio" />
                </div>
                <div className="space-y-1.5">
                  <Label>Costo</Label>
                  <Input type="number" value={productForm.costo}
                    onChange={e => setProductForm(p => ({ ...p, costo: e.target.value }))}
                    placeholder="3000" data-testid="product-form-costo" />
                </div>
                {!editingProduct && (
                  <div className="space-y-1.5">
                    <Label>Cantidad Inicial *</Label>
                    <Input type="number" value={productForm.cantidad_inicial}
                      onChange={e => setProductForm(p => ({ ...p, cantidad_inicial: e.target.value }))}
                      placeholder="50" data-testid="product-form-cantidad" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Stock Mínimo (alerta)</Label>
                  <Input type="number" value={productForm.stock_minimo}
                    onChange={e => setProductForm(p => ({ ...p, stock_minimo: e.target.value }))}
                    placeholder="5" data-testid="product-form-stock-minimo" />
                </div>
                <div className="space-y-1.5">
                  <Label>Unidad</Label>
                  <Select value={productForm.unidad} onValueChange={v => setProductForm(p => ({ ...p, unidad: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Unidad', 'Litro', 'Kilo', 'Paquete', 'Caja'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Descripción</Label>
                  <Input value={productForm.descripcion} onChange={e => setProductForm(p => ({ ...p, descripcion: e.target.value }))}
                    placeholder="Descripción opcional" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProductDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveProduct} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="product-form-save">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingProduct ? 'Guardar' : 'Crear Producto'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* STOCK ADJUSTMENT DIALOG */}
        <Dialog open={stockDialog} onOpenChange={setStockDialog}>
          <DialogContent className="max-w-sm" data-testid="stock-dialog">
            <DialogHeader>
              <DialogTitle>Ajustar Stock: {stockTarget?.nombre}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Stock actual: <strong>{stockTarget?.stock_actual} {stockTarget?.unidad}</strong></p>
              <div className="flex gap-2">
                <Button size="sm" variant={stockForm.tipo === 'entrada' ? 'default' : 'outline'}
                  onClick={() => setStockForm(f => ({ ...f, tipo: 'entrada' }))} className={stockForm.tipo === 'entrada' ? 'bg-green-600' : ''}>
                  <ArrowUpCircle className="h-4 w-4 mr-1" />Entrada
                </Button>
                <Button size="sm" variant={stockForm.tipo === 'salida' ? 'default' : 'outline'}
                  onClick={() => setStockForm(f => ({ ...f, tipo: 'salida' }))} className={stockForm.tipo === 'salida' ? 'bg-red-600' : ''}>
                  <ArrowDownCircle className="h-4 w-4 mr-1" />Salida
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label>Cantidad</Label>
                <Input type="number" value={stockForm.cantidad} onChange={e => setStockForm(f => ({ ...f, cantidad: e.target.value }))}
                  placeholder="10" data-testid="stock-cantidad" />
              </div>
              <div className="space-y-1.5">
                <Label>Motivo</Label>
                <Select value={stockForm.motivo} onValueChange={v => setStockForm(f => ({ ...f, motivo: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compra">Compra</SelectItem>
                    <SelectItem value="devolucion">Devolución</SelectItem>
                    <SelectItem value="dano">Daño/Pérdida</SelectItem>
                    <SelectItem value="ajuste_manual">Ajuste Manual</SelectItem>
                    <SelectItem value="vencimiento">Vencimiento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStockDialog(false)}>Cancelar</Button>
              <Button onClick={handleAdjustStock} className="bg-amber-600 hover:bg-amber-700" data-testid="stock-save">Aplicar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CHARGE TO ROOM DIALOG */}
        <Dialog open={chargeDialog} onOpenChange={setChargeDialog}>
          <DialogContent className="max-w-md" data-testid="charge-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Bed className="h-5 w-5 text-amber-600" />Cargar a Habitación</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-medium">{chargeProduct?.nombre}</p>
                <p className="text-sm text-muted-foreground">Precio: {pf(chargeProduct?.precio_venta)} | Stock: {chargeProduct?.stock_actual}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Habitación / Reserva *</Label>
                <Select value={chargeForm.reservation_id} onValueChange={v => setChargeForm(f => ({ ...f, reservation_id: v }))}>
                  <SelectTrigger data-testid="charge-reservation"><SelectValue placeholder="Seleccionar habitación..." /></SelectTrigger>
                  <SelectContent>
                    {reservations.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.room_name || 'Hab.'} - {r.guest_name} ({r.estado})
                      </SelectItem>
                    ))}
                    {reservations.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No hay habitaciones ocupadas</div>}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Cantidad</Label>
                  <Input type="number" min="1" value={chargeForm.cantidad}
                    onChange={e => setChargeForm(f => ({ ...f, cantidad: e.target.value }))} data-testid="charge-cantidad" />
                </div>
                <div className="space-y-1.5">
                  <Label>Total</Label>
                  <p className="text-lg font-bold pt-1">{pf((chargeProduct?.precio_venta || 0) * parseInt(chargeForm.cantidad || 0))}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Concepto</Label>
                <Input value={chargeForm.concepto} onChange={e => setChargeForm(f => ({ ...f, concepto: e.target.value }))} data-testid="charge-concepto" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setChargeDialog(false)}>Cancelar</Button>
              <Button onClick={handleCharge} className="bg-amber-600 hover:bg-amber-700" data-testid="charge-save">
                <ShoppingCart className="h-4 w-4 mr-2" />Cargar Consumo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DELETE DIALOG */}
        <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Producto</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Eliminar <strong>{deleteTarget?.nombre}</strong>? Se perderá el historial de movimientos asociado.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteProduct} className="bg-red-600 hover:bg-red-700" data-testid="delete-product-confirm">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
